import React, { useState } from 'react';
import { Box, Button, TextField, Typography, Alert } from '@mui/material';
import { useAuth } from '../context/AuthContext';

const ALLOWED_DOMAIN = '@fusionrm.com';

export const LoginForm: React.FC = () => {
	const { requestMagicLink } = useAuth();
	const [email, setEmail] = useState('');
	const [error, setError] = useState<string | null>(null);
	const [success, setSuccess] = useState<string | null>(null);
	const [isSubmitting, setIsSubmitting] = useState(false);

	const handleSubmit = async (event: React.FormEvent) => {
		event.preventDefault();
		setError(null);
		setSuccess(null);

		const normalizedEmail = email.trim().toLowerCase();
		if (!normalizedEmail.endsWith(ALLOWED_DOMAIN)) {
			setError(`Only ${ALLOWED_DOMAIN} email addresses are allowed.`);
			return;
		}

		setIsSubmitting(true);
		try {
			const senderAddress = await requestMagicLink(normalizedEmail);
			if (senderAddress) {
				setSuccess(`Check your email (from ${senderAddress}) for a magic sign-in link.`);
			} else {
				setSuccess('Check your email for a magic sign-in link.');
			}
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Failed to request magic link');
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<Box
			component="form"
			onSubmit={handleSubmit}
			display="flex"
			flexDirection="column"
			alignItems="center"
			justifyContent="center"
			minHeight="400px"
			gap={2}
			p={3}
		>
			<Typography variant="h5">Sign in</Typography>
			<Typography variant="body2" color="text.secondary">
				Use your {ALLOWED_DOMAIN} email address to receive a magic link.
			</Typography>
			<TextField
				label="Email"
				type="email"
				value={email}
				onChange={(e) => setEmail(e.target.value)}
				fullWidth
				sx={{ maxWidth: 400 }}
				required
			/>
			<Button type="submit" variant="contained" disabled={isSubmitting} sx={{ minWidth: 200 }}>
				{isSubmitting ? 'Sending...' : 'Send Magic Link'}
			</Button>
			{error && <Alert severity="error" sx={{ maxWidth: 500 }}>{error}</Alert>}
			{success && <Alert severity="success" sx={{ maxWidth: 500 }}>{success}</Alert>}
		</Box>
	);
};
